import { api, ApiError, type SessionUser } from '../io/api';

export interface AuthScreenOptions {
  /** Wird nach erfolgreichem Login/Registrierung aufgerufen (Overlay wird entfernt). */
  onSuccess(user: SessionUser): void;
  /** Overlay-Modus: schließt den Dialog, ohne den Bildschirm zu ersetzen (Esc/Klick daneben). */
  onCancel?(): void;
  /** Overlay statt Vollbild (z. B. „Projekt speichern“ über dem laufenden Editor). */
  asOverlay?: boolean;
  /** Startmodus — beim Speichern-Gate registrieren neue Nutzer direkt. */
  startMode?: 'login' | 'register';
  /** Hinweistext über dem Formular (z. B. „Zum Öffnen des Projekts anmelden“). */
  message?: string;
}

/**
 * Anmeldung/Registrierung — nur E-Mail und Passwort, kein Name (der Anzeigename
 * leitet sich serverseitig aus dem E-Mail-Prefix ab; für die Kollaboration wählt
 * man seinen Namen ohnehin frei im Teilen-Dialog).
 * Zwei Montagearten: Vollbild (Routen-Gate) oder Overlay über dem laufenden Editor
 * (Speichern-Gate im Entwurfsmodus).
 */
export class AuthScreen {
  private mode: 'login' | 'register' = 'login';
  private wrap: HTMLElement;
  private form: HTMLFormElement;
  private emailInput: HTMLInputElement;
  private passwordInput: HTMLInputElement;
  private errorText: HTMLParagraphElement;
  private submitBtn: HTMLButtonElement;
  private toggleBtn: HTMLButtonElement;
  private title: HTMLHeadingElement;
  private busy = false;
  private closed = false;

  constructor(root: HTMLElement, private options: AuthScreenOptions) {
    this.wrap = document.createElement('div');
    if (options.asOverlay) {
      this.wrap.className = 'overlay auth-overlay';
      this.wrap.addEventListener('mousedown', (e) => {
        if (e.target === this.wrap) this.close(false);
      });
      this.keyHandler = (e) => {
        if (e.key === 'Escape') {
          e.stopPropagation();
          this.close(false);
        }
      };
      document.addEventListener('keydown', this.keyHandler, true);
    } else {
      this.wrap.className = 'auth-screen';
    }

    const card = document.createElement('div');
    card.className = 'auth-card';

    const logo = document.createElement('div');
    logo.className = 'logo auth-logo';
    logo.innerHTML =
      '<svg viewBox="0 0 100 100" width="26" height="26"><rect x="8" y="30" width="70" height="45" rx="8" fill="#e0552c"/><circle cx="28" cy="78" r="9" fill="#222"/><circle cx="68" cy="78" r="9" fill="#222"/></svg><span>CampSketch</span>';
    card.appendChild(logo);

    this.title = document.createElement('h1');
    this.title.textContent = 'Anmelden';
    card.appendChild(this.title);

    if (options.message) {
      const hint = document.createElement('p');
      hint.className = 'auth-hint';
      hint.textContent = options.message;
      card.appendChild(hint);
    }

    this.errorText = document.createElement('p');
    this.errorText.className = 'auth-error';
    this.errorText.hidden = true;
    card.appendChild(this.errorText);

    this.form = document.createElement('form');
    this.form.noValidate = true;
    this.form.onsubmit = (e) => {
      e.preventDefault();
      void this.submit();
    };

    this.form.appendChild(this.field('E-Mail', (this.emailInput = this.input('email', 'name@beispiel.de', 254))));
    this.form.appendChild(this.field('Passwort', (this.passwordInput = this.input('password', '', 128))));

    this.submitBtn = document.createElement('button');
    this.submitBtn.type = 'submit';
    this.submitBtn.className = 'auth-submit';
    this.form.appendChild(this.submitBtn);
    card.appendChild(this.form);

    this.toggleBtn = document.createElement('button');
    this.toggleBtn.type = 'button';
    this.toggleBtn.className = 'auth-toggle';
    this.toggleBtn.onclick = () => this.setMode(this.mode === 'login' ? 'register' : 'login');
    card.appendChild(this.toggleBtn);

    if (options.asOverlay && options.onCancel) {
      const cancelBtn = document.createElement('button');
      cancelBtn.type = 'button';
      cancelBtn.className = 'auth-toggle';
      cancelBtn.textContent = 'Abbrechen';
      cancelBtn.onclick = () => this.close(false);
      card.appendChild(cancelBtn);
    }

    this.wrap.appendChild(card);
    root.appendChild(this.wrap);
    this.setMode(options.startMode ?? 'login');
  }

  private keyHandler: ((e: KeyboardEvent) => void) | null = null;

  private input(type: string, placeholder: string, maxLength: number): HTMLInputElement {
    const input = document.createElement('input');
    input.type = type;
    input.placeholder = placeholder;
    input.maxLength = maxLength;
    input.autocomplete = type === 'password' ? 'current-password' : 'email';
    return input;
  }

  private field(label: string, input: HTMLInputElement): HTMLLabelElement {
    const row = document.createElement('label');
    row.className = 'auth-field';
    row.append(label);
    row.appendChild(input);
    return row;
  }

  private setMode(mode: 'login' | 'register'): void {
    this.mode = mode;
    this.title.textContent = mode === 'login' ? 'Anmelden' : 'Konto erstellen';
    this.passwordInput.autocomplete = mode === 'login' ? 'current-password' : 'new-password';
    if (mode === 'register') this.passwordInput.minLength = 8;
    else this.passwordInput.removeAttribute('minlength');
    this.submitBtn.textContent = mode === 'login' ? 'Anmelden' : 'Konto erstellen';
    this.toggleBtn.textContent = mode === 'login' ? 'Noch kein Konto? Jetzt registrieren' : 'Schon ein Konto? Zur Anmeldung';
    this.setError(null);
  }

  private setError(message: string | null): void {
    this.errorText.textContent = message ?? '';
    this.errorText.hidden = !message;
  }

  /** Overlay entfernen (finished = nach Erfolg darf nicht doppelt aufgerufen werden). */
  private close(finished: boolean): void {
    if (this.closed) return;
    this.closed = true;
    if (this.keyHandler) document.removeEventListener('keydown', this.keyHandler, true);
    this.wrap.remove();
    if (!finished) this.options.onCancel?.();
  }

  private async submit(): Promise<void> {
    if (this.busy || this.closed) return;
    const email = this.emailInput.value.trim();
    const password = this.passwordInput.value;
    if (!email) return this.setError('Bitte eine E-Mail-Adresse angeben');
    if (this.mode === 'register' && password.length < 8) return this.setError('Das Passwort braucht mindestens 8 Zeichen');
    if (!password) return this.setError('Bitte ein Passwort angeben');

    this.busy = true;
    this.submitBtn.disabled = true;
    try {
      const { user } = this.mode === 'login' ? await api.login(email, password) : await api.register(email, password);
      this.close(true);
      this.options.onSuccess(user);
    } catch (err) {
      this.setError(err instanceof ApiError ? err.message : 'Verbindung fehlgeschlagen — bitte später noch einmal versuchen');
      this.busy = false;
      this.submitBtn.disabled = false;
    }
  }
}
